import React, { useState } from 'react';
import { Check } from 'lucide-react';
import NavbarSection from '../components/NavbarSection';
import FooterSection from '../components/FooterSection';
import cpa_hero_image from '../assets/cpa-hero-image.png';
import { httpsCallable } from 'firebase/functions';
import {  functions } from '../firebase';

function CPAPage() {
  const [formData, setFormData] = useState({
    name: '',
    firmName: '',
    email: '',
    website: '',
    clients: '',
    businessType: '',
    interest: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    const { id, name, value, type } = e.target;
    const fieldName = type === 'radio' ? name : id;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Validate required fields
      if (!formData.name || !formData.firmName || !formData.email) {
        setMessage({ type: 'error', text: 'Please fill in all required fields' });
        setLoading(false);
        return;
      }

      // Call Firebase Cloud Function to send email
      const sendCPAPartnerEmail = httpsCallable(functions, 'sendCPAPartnerEmail');
      const result = await sendCPAPartnerEmail(formData);

      if (result.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Thank you! Your request has been submitted successfully. We will contact you soon.' 
        });
        
        // Reset form
        setFormData({
          name: '',
          firmName: '',
          email: '',
          website: '',
          clients: '',
          businessType: '',
          interest: ''
        });

        // Scroll to top of form to show success message
        document.getElementById('cpa-form').scrollIntoView({ behavior: 'smooth' });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'An error occurred. Please try again or contact us directly.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Top Section with Navbar */}
      <div id="top-page" className="position-relative page-position-style">
        <div className="vector-layer">
          <NavbarSection />
        </div>
      </div>

      {/* Hero Section */}
      <section className="cpa-hero-section position-relative page-position-style">
        <div className="vector-layer">
          <div className="container">
            <div className="row align-items-center">
              {/* Left Content */}
              <div className="col-lg-6 col-md-12">
                <div className="cpa-hero-content">
                  <h1 className="cpa-hero-title">
                    The problem CPAs face every tax season
                  </h1>
                  <p className="cpa-hero-subtitle">
                    Because most clients often arrive with incomplete, inconsistent, or unstructured 
                    records — creating unnecessary friction during tax prep.
                  </p>
                  <button 
                    className="cpa-request-btn"
                    onClick={() => {
                      document.getElementById('cpa-form').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Request CPA Partner Access
                  </button>
                </div>
              </div>

              {/* Right Image */}
              <div className="col-lg-6 col-md-12">
                <div className="cpa-hero-image">
                  <img 
                    src={cpa_hero_image}
                    alt="CPA working on taxes"
                    className="img-fluid"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems & Solutions Section */}
      <section className="cpa-content-section">
        <div className="container">
          <div className="row g-5">
            {/* Left Column - Problems */}
            <div className="col-lg-6 col-md-12">
              <div className="cpa-problems-card">
                <h2 className="cpa-section-title">
                  What usually goes wrong before clients reach your desk
                </h2>
                
                <div className="cpa-problems-list">
                  <div className="cpa-problem-item">
                    <div className="problem-icon">
                      <Check size={20} />
                    </div>
                    <p>Receipts scattered across emails, photos, and apps</p>
                  </div>

                  <div className="cpa-problem-item">
                    <div className="problem-icon">
                      <Check size={20} />
                    </div>
                    <p>Expenses with no explanation or business context</p>
                  </div>

                  <div className="cpa-problem-item">
                    <div className="problem-icon">
                      <Check size={20} />
                    </div>
                    <p>Missing or inconsistent documentation</p>
                  </div>

                  <div className="cpa-problem-item">
                    <div className="problem-icon">
                      <Check size={20} />
                    </div>
                    <p>Last-minute follow-ups that delay prep and increase audit risk</p>
                  </div>
                </div>

                <div className="cpa-result-box">
                  <p className="result-label">The result:</p>
                  <p className="result-text">
                    extra back-and-forth, avoidable delays, and higher audit exposure.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Workflow */}
            <div className="col-lg-6 col-md-12">
              <div className="cpa-workflow-card">
                <h2 className="cpa-section-title">
                  How it fits into your workflow
                </h2>

                <div className="workflow-steps">
                  <div className="workflow-step">
                    <div className="step-number-badge">1</div>
                    <div className="step-content">
                      <h3 className="step-title">Capture</h3>
                      <p className="step-description">
                        Clients upload their business accounts (read-only). Expenses 
                        are pulled automatically.
                      </p>
                    </div>
                  </div>

                  <div className="workflow-step">
                    <div className="step-number-badge">2</div>
                    <div className="step-content">
                      <h3 className="step-title">Review & Document</h3>
                      <p className="step-description">
                        Clients review expenses, attach receipts, and add business 
                        context — on their own time.
                      </p>
                    </div>
                  </div>

                  <div className="workflow-step">
                    <div className="step-number-badge">3</div>
                    <div className="step-content">
                      <h3 className="step-title">Organized Records</h3>
                      <p className="step-description">
                        Expenses remain categorized and documented for tax time, 
                        year-end cleanups, or audit support.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why CPAs Recommend Section */}
      <section className="cpa-recommend-section">
        <div className="container">
          <h2 className="text-center recommend-title">
            Why CPAs recommend WriteOffGenie
          </h2>

          <div className="row justify-content-center mt-5">
            <div className="col-lg-8">
              <div className="recommend-list">
                <div className="recommend-item">
                  <div className="recommend-icon">
                    <Check size={20} />
                  </div>
                  <div className="recommend-content">
                    <h5 className="recommend-item-title">Fewer follow-ups</h5>
                    <p className="recommend-item-desc">receipts and supporting arrive organized</p>
                  </div>
                </div>

                <div className="recommend-item">
                  <div className="recommend-icon">
                    <Check size={20} />
                  </div>
                  <div className="recommend-content">
                    <h5 className="recommend-item-title">Cleaner books</h5>
                    <p className="recommend-item-desc">less guessing, fewer "what was this for?" conversations</p>
                  </div>
                </div>

                <div className="recommend-item">
                  <div className="recommend-icon">
                    <Check size={20} />
                  </div>
                  <div className="recommend-content">
                    <h5 className="recommend-item-title">Audit-ready evidence</h5>
                    <p className="recommend-item-desc">documentation creates confidence</p>
                  </div>
                </div>

                <div className="recommend-item">
                  <div className="recommend-icon">
                    <Check size={20} />
                  </div>
                  <div className="recommend-content">
                    <h5 className="recommend-item-title">Reduced client anxiety</h5>
                    <p className="recommend-item-desc">clients feel prepared instead of scrambling</p>
                  </div>
                </div>

                <div className="recommend-item">
                  <div className="recommend-icon">
                    <Check size={20} />
                  </div>
                  <div className="recommend-content">
                    <h5 className="recommend-item-title">Works alongside you</h5>
                    <p className="recommend-item-desc">can integrate to help you file or advise</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="cpa-disclaimer-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <p className="disclaimer-subtitle">Clear boundaries. No grey areas.</p>
              <h2 className="disclaimer-title">
                WriteOffGenie is a documentation and organization tool. It does not provide tax 
                advice, filing services, or recommendations on tax treatment.
              </h2>
              <p className="disclaimer-note">
                Only works for information and responsibility over all filings and decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request CPA Partner Access Form */}
      <section className="cpa-form-section" id="cpa-form">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7">
              <h2 className="text-center form-section-title">
                Request CPA Partner Access
              </h2>
              <p className="text-center form-section-subtitle">
                Leave your WriteOffGenie contact information/contact us to set up your firm
              </p>

              <form className="cpa-access-form" onSubmit={handleSubmit}>
                {message.text && (
                  <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    id="name" 
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="firmName">Firm name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    id="firmName" 
                    placeholder="or solo practitioner (enter name)"
                    value={formData.firmName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    id="email" 
                    placeholder="hello@cpa.co"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <small className="form-text">We only use this to connect on partnership setup</small>
                </div>

                <div className="form-group">
                  <label htmlFor="website">Website (Optional)</label>
                  <input 
                    type="url" 
                    className="form-control" 
                    id="website" 
                    placeholder="e.g. yourfirm.com"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="clients">Approximate clients</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    id="clients" 
                    placeholder="e.g. 50 clients"
                    value={formData.clients}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="businessType">Kind (ex. typical of your work)</label>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="businessType" 
                      id="individual" 
                      value="individual"
                      checked={formData.businessType === 'individual'}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="individual">
                      Individual
                    </label>
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="radio" 
                      name="businessType" 
                      id="smallBusiness" 
                      value="small-business"
                      checked={formData.businessType === 'small-business'}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="smallBusiness">
                      S-Corps
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="interest">Where's your biggest interest with documentation/proof?</label>
                  <textarea 
                    className="form-control" 
                    id="interest" 
                    rows="4"
                    placeholder="Tell us how you envision using our service"
                    value={formData.interest}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <button type="submit" className="btn cpa-submit-btn" disabled={loading}>
                  {loading ? 'Submitting...' : 'Request CPA Partner Access'}
                </button>

                <p className="form-privacy-text text-center">
                  We protect your data and partner privacy. Your information is confidential and secure.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <FooterSection />

      <style>{`
        /* Hero Section */
        .cpa-hero-section {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 80px 0 60px;
          overflow: hidden;
        }

        .cpa-hero-content {
          padding: 20px 0;
        }

        .cpa-hero-title {
          font-size: 2.8rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .cpa-hero-subtitle {
          font-size: 1.1rem;
          color: #4a5568;
          line-height: 1.7;
          margin-bottom: 2rem;
          max-width: 500px;
        }

        .cpa-request-btn {
          background: #011c39;
          color: #ffffff;
          border: none;
          padding: 14px 32px;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(1, 28, 57, 0.2);
        }

        .cpa-request-btn:hover {
          background: #022952;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(1, 28, 57, 0.3);
        }

        .cpa-hero-image {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
        }

        .cpa-hero-image img {
          max-width: 100%;
          height: auto;
          border-radius: 20px;
          transition: transform 0.3s ease;
        }

        .cpa-hero-image img:hover {
          transform: scale(1.02);
        }

        /* Content Section */
        .cpa-content-section {
          padding: 80px 0;
          background: #ffffff;
        }

        .cpa-section-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 2rem;
          line-height: 1.3;
        }

        /* Problems Card */
        .cpa-problems-card {
          background: #ffffff;
          padding: 40px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .cpa-problems-list {
          margin-bottom: 2.5rem;
        }

        .cpa-problem-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 1.5rem;
        }

        .problem-icon {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          background: #22c55e;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-top: 2px;
        }

        .cpa-problem-item p {
          font-size: 1rem;
          color: #374151;
          line-height: 1.6;
          margin: 0;
        }

        .cpa-result-box {
          background: #fff7ed;
          border-left: 4px solid #f59e0b;
          padding: 20px;
          border-radius: 8px;
        }

        .result-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #92400e;
          margin-bottom: 8px;
        }

        .result-text {
          font-size: 1rem;
          color: #78350f;
          margin: 0;
          line-height: 1.5;
        }

        /* Workflow Card */
        .cpa-workflow-card {
          background: #ffffff;
          padding: 40px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .workflow-steps {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .workflow-step {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .step-number-badge {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          background: #011c39;
          color: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .step-content {
          flex: 1;
        }

        .step-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .step-description {
          font-size: 0.95rem;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Why CPAs Recommend Section */
        .cpa-recommend-section {
          padding: 80px 0;
          background: #ffffff;
        }

        .recommend-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 3rem;
        }

        .recommend-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .recommend-item {
          display: flex;
          align-items: flex-start;
          gap: 15px;
          padding: 1rem 0;
        }

        .recommend-icon {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          background: #22c55e;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .recommend-content {
          flex: 1;
        }

        .recommend-item-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .recommend-item-desc {
          font-size: 0.95rem;
          color: #6b7280;
          margin: 0;
        }

        /* Disclaimer Section */
        .cpa-disclaimer-section {
          padding: 80px 0;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
        }

        .disclaimer-subtitle {
          font-size: 0.95rem;
          color: #6b7280;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .disclaimer-title {
          font-size: 2rem;
          font-weight: 700;
          color: #0369a1;
          line-height: 1.4;
          margin-bottom: 1.5rem;
        }

        .disclaimer-note {
          font-size: 0.9rem;
          color: #64748b;
          font-style: italic;
        }

        /* Form Section */
        .cpa-form-section {
          padding: 80px 0;
          background: #ffffff;
        }

        .form-section-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 1rem;
        }

        .form-section-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin-bottom: 3rem;
        }

        .cpa-access-form {
          background: #f9fafb;
          padding: 40px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .cpa-access-form .form-group {
          margin-bottom: 1.5rem;
        }

        .cpa-access-form label {
          font-size: 0.95rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
          display: block;
        }

        .cpa-access-form .form-control {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          width: 100%;
        }

        .cpa-access-form .form-control:focus {
          border-color: #011c39;
          box-shadow: 0 0 0 3px rgba(1, 28, 57, 0.1);
          outline: none;
        }

        .cpa-access-form .form-text {
          font-size: 0.85rem;
          color: #9ca3af;
          margin-top: 6px;
          display: block;
        }

        .cpa-access-form textarea.form-control {
          resize: vertical;
          min-height: 100px;
        }

        .form-check {
          margin-bottom: 10px;
        }

        .form-check-input {
          margin-top: 4px;
        }

        .form-check-label {
          font-weight: 400;
          color: #4b5563;
          margin-left: 8px;
        }

        .cpa-submit-btn {
          width: 100%;
          background: #011c39;
          color: #ffffff;
          border: none;
          padding: 14px 32px;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
          margin-bottom: 1rem;
        }

        .cpa-submit-btn:hover:not(:disabled) {
          color: #ffffff;
          background: #022952;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(1, 28, 57, 0.3);
        }

        .cpa-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-privacy-text {
          font-size: 0.85rem;
          color: #9ca3af;
          margin-top: 1rem;
        }

        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .alert-success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #10b981;
        }

        .alert-danger {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        /* Responsive Design */
        @media (max-width: 991px) {
          .cpa-hero-title {
            font-size: 2.3rem;
          }

          .cpa-hero-subtitle {
            font-size: 1rem;
          }

          .cpa-hero-image {
            margin-top: 40px;
          }

          .cpa-section-title {
            font-size: 1.6rem;
          }
        }

        @media (max-width: 768px) {
          .cpa-hero-section {
            padding: 60px 0 40px;
          }

          .cpa-hero-title {
            font-size: 2rem;
            text-align: center;
          }

          .cpa-hero-subtitle {
            font-size: 1rem;
            text-align: center;
            max-width: 100%;
          }

          .cpa-request-btn {
            width: 100%;
            display: block;
          }

          .cpa-problems-card,
          .cpa-workflow-card {
            padding: 30px 20px;
          }

          .cpa-content-section {
            padding: 60px 0;
          }

          .cpa-section-title {
            font-size: 1.5rem;
            text-align: center;
          }

          .step-number-badge {
            width: 40px;
            height: 40px;
            font-size: 1.25rem;
          }

          .step-title {
            font-size: 1.1rem;
          }

          .step-description {
            font-size: 0.9rem;
          }

          .recommend-title {
            font-size: 1.6rem;
          }

          .recommend-item-title {
            font-size: 1rem;
          }

          .disclaimer-title {
            font-size: 1.5rem;
          }

          .form-section-title {
            font-size: 1.6rem;
          }

          .cpa-access-form {
            padding: 24px 16px;
          }
        }

        @media (max-width: 576px) {
          .cpa-hero-title {
            font-size: 1.75rem;
          }

          .cpa-hero-subtitle {
            font-size: 0.95rem;
          }

          .cpa-request-btn {
            padding: 12px 24px;
            font-size: 0.95rem;
          }

          .cpa-problems-card,
          .cpa-workflow-card {
            padding: 24px 16px;
          }

          .cpa-problem-item {
            gap: 10px;
          }

          .problem-icon {
            width: 24px;
            height: 24px;
          }

          .cpa-problem-item p {
            font-size: 0.95rem;
          }

          .result-text {
            font-size: 0.95rem;
          }

          .cpa-recommend-section,
          .cpa-disclaimer-section,
          .cpa-form-section {
            padding: 60px 0;
          }

          .recommend-title {
            font-size: 1.5rem;
          }

          .disclaimer-title {
            font-size: 1.3rem;
          }

          .form-section-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </>
  );
}

export default CPAPage;